/**
 * VariantPickerSheet — Blinkit-style bottom sheet showing all variants of a
 * product with their own image, price, discount and ADD/qty stepper.
 *
 * Opened from ProductCard when a product has_variants → tap "ADD" reveals
 * the sheet instead of navigating to PDP.
 */
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, money, errMsg } from "@/lib/api";
import { Package, Timer } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import QtyStepper from "@/components/customer/QtyStepper";
import { useStore } from "@/contexts/StoreContext";

export default function VariantPickerSheet({ product, open, onOpenChange }) {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const { etaMinutes } = useStore();
  const eta = etaMinutes || 15;

  useEffect(() => {
    if (!open || !product?.id) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/products/${product.id}?with_variants=true`);
        if (cancelled) return;
        const rows = (data.variants || []).filter((v) => v.is_active !== false);
        setVariants(rows);
      } catch (e) {
        if (!cancelled) toast.error(errMsg(e, "Could not load variants"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, product?.id]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl max-h-[85dvh] overflow-y-auto p-0"
        data-testid="variant-picker-sheet"
      >
        <SheetHeader className="px-4 md:px-5 pt-4 pb-2 border-b sticky top-0 bg-card z-10">
          <SheetTitle className="text-base md:text-lg font-display font-semibold text-left leading-tight pr-6">
            {product?.name}
          </SheetTitle>
          <p className="text-[11px] text-muted-foreground text-left">Choose a pack size</p>
        </SheetHeader>

        <div className="px-3 md:px-5 py-3 space-y-2">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-xl border">
                <Skeleton className="w-16 h-16 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="w-16 h-9" />
              </div>
            ))
          ) : variants.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">No variants available.</div>
          ) : (
            variants.map((v) => {
              const disc = v.mrp && Number(v.mrp) > Number(v.selling_price)
                ? Math.round(((v.mrp - v.selling_price) / v.mrp) * 100) : 0;
              const sizeLabel = v.pack_size || (v.weight ? `${v.weight} ${v.weight_unit || ""}`.trim() : v.name);
              // Reuse QtyStepper with a synthetic product carrying variantId — cart lines are variant-aware.
              const stepperProduct = { ...product, name: `${product.name} · ${sizeLabel}` };
              return (
                <div
                  key={v.id}
                  className="flex items-center gap-3 p-2 rounded-xl border bg-card hover:border-primary/40 transition"
                  data-testid={`variant-option-${v.id}`}
                >
                  <div className="w-16 h-16 md:w-[72px] md:h-[72px] rounded-lg overflow-hidden bg-secondary/40 grid place-items-center relative shrink-0">
                    {(v.image_url || product?.thumbnail_url)
                      ? <img src={v.image_url || product.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      : <Package className="w-5 h-5 text-primary/40" />}
                    {disc > 0 && (
                      <div className="absolute top-0.5 left-0.5 bg-gradient-to-b from-primary to-[hsl(var(--brand-red-deep))] text-white text-[9px] font-black leading-tight text-center rounded px-1 py-0.5">
                        {disc}%<br />OFF
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{sizeLabel}</div>
                    <div className="inline-flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5">
                      <Timer className="w-2.5 h-2.5 text-primary" />{eta} MINS
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-sm font-bold tabular-nums">{money(v.selling_price)}</span>
                      {disc > 0 && (
                        <span className="text-[11px] text-muted-foreground line-through tabular-nums">{money(v.mrp)}</span>
                      )}
                    </div>
                  </div>
                  <QtyStepper
                    product={stepperProduct}
                    variantId={v.id}
                    size="sm"
                    testIdPrefix={`variant-qty-${v.id}`}
                    className="min-w-[72px]"
                  />
                </div>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
